import type { selector } from '../selector';

import { keys, isNil, compact } from '@/utilities/fp';
import { isCurrencyInfo } from '@/utilities/isCurrencyInfo';

import { bisector } from 'd3-array';
import { NODE_NAMES, GROUP_TYPES, THIN_HEIGHT_BUFFER } from '../const';
import type { SankeyData } from '../types';

import type {
  CompanyTimeSeries,
  TimeSeriesItem,
} from '../../../../../hooks/useCompanyTimeSeries';

import { getTodayDate } from '../utils/utils';

type RawDataType = ReturnType<typeof selector>;
type SafeRawDataType = Exclude<RawDataType, undefined>;
type ExtractNestedTypeFromA<T> = T extends { [key: string]: infer U }
  ? U
  : never;
type IndustryAnalysisPastType = SafeRawDataType['industryAnalysisPast'];
type RevenueSegmentsType = ExtractNestedTypeFromA<IndustryAnalysisPastType>;
type SafeRevenueSegmentsType = Exclude<RevenueSegmentsType, null>;

export const calcTotalRevenue = (
  revenueSegments: SafeRevenueSegmentsType,
  unit: number
) =>
  keys(revenueSegments).reduce((acc, key) => {
    const segmentRevenue = isNil(revenueSegments[key])
      ? 0
      : revenueSegments[key];
    return acc + segmentRevenue * unit;
  }, 0);

export const initRevenueSegmentData = (rawData: SafeRawDataType) => {
  const industryAnalysisPast = rawData.industryAnalysisPast || {};
  const revenueSegments =
    (industryAnalysisPast && industryAnalysisPast['revenue_segments']) || {};

  const unit = rawData.pastRevenueUnit || 1;

  const totalRevenueIn = calcTotalRevenue(revenueSegments, unit);

  const totalRevenueOut = rawData.revenue;

  const validKeys = keys(revenueSegments).filter(
    (key) => !isNil(revenueSegments[key])
  );

  const sortedKeys = validKeys.sort((keyA, keyB) => {
    return revenueSegments[keyB] - revenueSegments[keyA];
  });

  const positiveRevenueKeys = sortedKeys.filter(
    (key) => revenueSegments[key] >= 0
  );

  const negativeRevenueKeys = sortedKeys.filter(
    (key) => revenueSegments[key] < 0
  );

  const shouldShowBreakdown = validKeys.length > 0;

  const isShownElimination =
    shouldShowBreakdown && totalRevenueIn > totalRevenueOut;

  const targetForEachSegment = NODE_NAMES.REVENUE;

  // Other elimination node
  const otherEliminationRevenueNode = isShownElimination && {
    name: NODE_NAMES.REVENUE_ELIMINATION,
    key: 'Other Elimination',
    groupType: GROUP_TYPES.REVENUE_ELIMINATION,
  };

  const otherEliminationRevenueInLink = isShownElimination && {
    source: NODE_NAMES.REVENUE,
    target: NODE_NAMES.REVENUE_ELIMINATION,
    value: totalRevenueIn - totalRevenueOut,
    groupType: GROUP_TYPES.INCOME,
  };

  const isShownOtherRevenue =
    shouldShowBreakdown && totalRevenueIn < totalRevenueOut;

  const totalDisplayIncome = 5;

  const isGroupedRevenue = isShownOtherRevenue
    ? positiveRevenueKeys.length > totalDisplayIncome - 1
    : positiveRevenueKeys.length > totalDisplayIncome;

  const otherRevenueNode = isShownOtherRevenue &&
    !isGroupedRevenue && {
      name: NODE_NAMES.OTHER_REVENUE,
      key: 'Other',
      groupType: GROUP_TYPES.INCOME,
    };

  const totalOtherNodeValue = totalRevenueOut - totalRevenueIn;
  const otherRevenueOutLink = isShownOtherRevenue &&
    !isGroupedRevenue && {
      source: NODE_NAMES.OTHER_REVENUE,
      target: targetForEachSegment,
      value: totalOtherNodeValue,
      groupType: GROUP_TYPES.INCOME,
    };

  const mainPositiveRevenueKeys = isGroupedRevenue
    ? positiveRevenueKeys.slice(0, totalDisplayIncome - 1)
    : positiveRevenueKeys;
  const othersPositiveRevenueKeys = isGroupedRevenue
    ? positiveRevenueKeys.slice(totalDisplayIncome - 1)
    : [];

  const othersPositiveRevenueSubTotal = othersPositiveRevenueKeys.reduce(
    (acc, key) => {
      return acc + revenueSegments[key];
    },
    0
  );
  const othersPositiveRevenueTotal = isShownOtherRevenue
    ? othersPositiveRevenueSubTotal * unit + totalOtherNodeValue
    : othersPositiveRevenueSubTotal * unit;

  const mainPositiveRevenueNodes = mainPositiveRevenueKeys.map((key) => {
    return {
      name: key.toLowerCase(),
      key,
      groupType: GROUP_TYPES.INCOME,
    };
  });

  const othersPositiveRevenueNode = {
    name: 'other_segments',
    key: 'Other segments',
    value: othersPositiveRevenueTotal,
    groupType: GROUP_TYPES.INCOME,
  };

  const positiveRevenueNodes = [
    ...mainPositiveRevenueNodes,
    isGroupedRevenue && othersPositiveRevenueNode,
  ];

  const negativeRevenueNodes = negativeRevenueKeys.map((key) => ({
    name: key.toLowerCase(),
    key,
    groupType: GROUP_TYPES.REVENUE_ELIMINATION,
  }));

  const mainPositiveRevenueLinks = mainPositiveRevenueKeys.map((key) => {
    return {
      source: key.toLowerCase(),
      target: targetForEachSegment,
      value: revenueSegments[key] * unit,
      groupType: GROUP_TYPES.INCOME,
    };
  });

  const othersPositiveRevenueLinks = {
    source: 'other_segments',
    target: targetForEachSegment,
    value: othersPositiveRevenueTotal,
    groupType: GROUP_TYPES.INCOME,
  };

  const positiveRevenueLinks = [
    ...mainPositiveRevenueLinks,
    isGroupedRevenue && othersPositiveRevenueLinks,
  ];

  const negativeRevenueLinks = negativeRevenueKeys.map((key) => ({
    source: targetForEachSegment,
    target: key.toLowerCase(),
    value: Math.abs(revenueSegments[key]) * unit,
    groupType: GROUP_TYPES.REVENUE_ELIMINATION,
  }));

  const revenueNode = {
    name: NODE_NAMES.REVENUE,
    key: 'Revenue',
    groupType: GROUP_TYPES.INCOME,
  };

  return {
    nodes: compact([...positiveRevenueNodes, otherRevenueNode, revenueNode]),
    links: compact([...positiveRevenueLinks, otherRevenueOutLink]),
    negativeNodes: compact([
      ...negativeRevenueNodes,
      otherEliminationRevenueNode,
    ]),
    negativeLinks: compact([
      ...negativeRevenueLinks,
      otherEliminationRevenueInLink,
    ]),
  };
};

export const createExpenseLink = ({ nodeName, value }) => {
  const source = value > 0 ? NODE_NAMES.EXPENSES : nodeName;
  const target = value > 0 ? nodeName : NODE_NAMES.EXPENSES;
  const valueAbs = Math.abs(value);
  const groupType = GROUP_TYPES.EXPENSES;
  return {
    source,
    target,
    value: valueAbs,
    groupType,
  };
};

export const getExpensesOutData = (rawData: SafeRawDataType) => {
  const industryAnalysisPast = rawData?.industryAnalysisPast || {};

  const rDExpenses = industryAnalysisPast['r_d_expense'] as number;

  const salesMarketingExpenses = industryAnalysisPast[
    'sales_marketing'
  ] as number;
  const generalAdminExpenses = industryAnalysisPast[
    'general_administrative'
  ] as number;
  const dAexpense = industryAnalysisPast['d_a_expense'] as number;
  const nonOpsExpense = industryAnalysisPast['non_op_expense'] as number;

  const generalAdminNode = !isNil(generalAdminExpenses) && {
    name: NODE_NAMES.GENERAL_ADMIN_EXPENSE,
    key: 'General & Administrative',
    groupType: GROUP_TYPES.EXPENSES,
  };

  const researchAndDevelopmentNode = !isNil(rDExpenses) && {
    name: NODE_NAMES.RESEARCH_DEVELOPMENT_EXPENSE,
    key: 'Research & Development',
    groupType: GROUP_TYPES.EXPENSES,
  };

  const salesMarketingNode = !isNil(salesMarketingExpenses) && {
    name: NODE_NAMES.SALES_MARKETING_EXPENSE,
    key: 'Sales & Marketing',
    groupType: GROUP_TYPES.EXPENSES,
  };

  const depreciationAmortisationNode = !isNil(dAexpense) && {
    name: NODE_NAMES.DEPRECATION_AMORTISATION_EXPENSE,
    key: 'Depreciation & Amortisation',
    groupType: GROUP_TYPES.EXPENSES,
  };

  const nonOperatingExpenseNode = !isNil(nonOpsExpense) && {
    name: NODE_NAMES.NON_OPERATING_EXPENSE,
    key: 'Non-Operating Expense',
    groupType: GROUP_TYPES.EXPENSES,
  };

  const generalAdminLink =
    !isNil(generalAdminExpenses) &&
    createExpenseLink({
      nodeName: NODE_NAMES.GENERAL_ADMIN_EXPENSE,
      value: generalAdminExpenses * rawData.pastRevenueUnit,
    });

  const researchAndDevelopmentLink =
    !isNil(rDExpenses) &&
    createExpenseLink({
      nodeName: NODE_NAMES.RESEARCH_DEVELOPMENT_EXPENSE,
      value: rDExpenses * rawData.pastRevenueUnit,
    });

  const salesMarketingLink =
    !isNil(salesMarketingExpenses) &&
    createExpenseLink({
      nodeName: NODE_NAMES.SALES_MARKETING_EXPENSE,
      value: salesMarketingExpenses * rawData.pastRevenueUnit,
    });

  const depreciationAmortisationLink =
    !isNil(dAexpense) &&
    createExpenseLink({
      nodeName: NODE_NAMES.DEPRECATION_AMORTISATION_EXPENSE,
      value: dAexpense * rawData.pastRevenueUnit,
    });

  const nonOperatingExpenseLink =
    !isNil(nonOpsExpense) &&
    createExpenseLink({
      nodeName: NODE_NAMES.NON_OPERATING_EXPENSE,
      value: nonOpsExpense * rawData.pastRevenueUnit,
    });

  const expensesArr = [
    rDExpenses,
    generalAdminExpenses,
    salesMarketingExpenses,
    dAexpense,
    nonOpsExpense,
  ];

  const validExpensesArr = expensesArr.filter((expense) => !isNil(expense));

  const sumOfExpenses = validExpensesArr.reduce((acc, curr) => acc + curr, 0);

  const otherExpenses =
    rawData.expenses - sumOfExpenses * rawData.pastRevenueUnit;

  const isShownOtherExpenses = validExpensesArr.length > 0 && otherExpenses > 0;
  const otherExpenseNode = isShownOtherExpenses && {
    name: NODE_NAMES.OTHER_EXPENSE,
    key: 'Other',
    groupType: GROUP_TYPES.EXPENSES,
  };

  const otherExpenseLink = isShownOtherExpenses && {
    source: NODE_NAMES.EXPENSES,
    target: NODE_NAMES.OTHER_EXPENSE,
    value: otherExpenses,
    groupType: GROUP_TYPES.EXPENSES,
  };

  const nodes = compact([
    generalAdminNode,
    researchAndDevelopmentNode,
    salesMarketingNode,
    depreciationAmortisationNode,
    nonOperatingExpenseNode,
    otherExpenseNode,
  ]);

  const links = compact([
    generalAdminLink,
    researchAndDevelopmentLink,
    salesMarketingLink,
    depreciationAmortisationLink,
    nonOperatingExpenseLink,
    otherExpenseLink,
  ]);

  return {
    nodes,
    links,
  };
};

export const isThinChart = (sankeyData: SankeyData) => {
  const nodeHeights = sankeyData.nodes.map((node) =>
    Math.max(1, node.y1 - node.y0)
  );
  const maxNodeHeight = Math.max(...nodeHeights);
  return maxNodeHeight < THIN_HEIGHT_BUFFER;
};

export const getGrossProfitNodes = (grossProfit: number) => {
  const isNegativeGrossProfit = grossProfit < 0;

  const grossProfitNode = !isNegativeGrossProfit && {
    name: NODE_NAMES.GROSS_PROFIT,
    key: 'Gross Profit',
    groupType: GROUP_TYPES.COST_REVENUE,
  };

  const lossGrossProfitNode = isNegativeGrossProfit && {
    name: NODE_NAMES.LOSS_GROSS_PROFIT,
    key: 'Gross Profit',
    groupType: GROUP_TYPES.LOSS,
  };

  return compact([grossProfitNode, lossGrossProfitNode]);
};

type GetRevenueOutLinksParams = {
  grossProfit: number;
  costOfRevenue: number;
  revenue: number;
};
export const getRevenueOutLinks = ({
  grossProfit,
  costOfRevenue,
  revenue,
}: GetRevenueOutLinksParams) => {
  const isNegativeGrossProfit = grossProfit < 0;

  const revenueToGrossProfitLink = !isNegativeGrossProfit && {
    source: NODE_NAMES.REVENUE,
    target: NODE_NAMES.GROSS_PROFIT,
    value: grossProfit,
    groupType: GROUP_TYPES.COST_REVENUE,
  };

  const revenueToCostOfSalesLink = {
    source: NODE_NAMES.REVENUE,
    target: NODE_NAMES.COST_OF_SALES,
    value: !isNegativeGrossProfit ? costOfRevenue : revenue,
    groupType: GROUP_TYPES.EXPENSES,
  };

  return compact([revenueToGrossProfitLink, revenueToCostOfSalesLink]);
};

type GetEarningsLossOutLinksParams = {
  earnings: number;
  expenses: number;
  grossProfit: number;
};
export const getEarningsLossOutLinks = ({
  earnings,
  expenses,
  grossProfit,
}: GetEarningsLossOutLinksParams) => {
  const isNegativeGrossProfit = grossProfit < 0;
  const isShowEaningsLoss = earnings < 0;

  const earningsLossToExpenseLink = isShowEaningsLoss && {
    source: NODE_NAMES.EARNINGS_LOSS,
    target: NODE_NAMES.EXPENSES,
    value: isNegativeGrossProfit ? Math.abs(expenses) : Math.abs(earnings),
    groupType: GROUP_TYPES.LOSS,
  };

  const earningsLossToGrossProfitLink = isNegativeGrossProfit &&
    isShowEaningsLoss && {
      source: NODE_NAMES.EARNINGS_LOSS,
      target: NODE_NAMES.LOSS_GROSS_PROFIT,
      value: Math.abs(grossProfit),
      groupType: GROUP_TYPES.LOSS,
    };

  return compact([earningsLossToGrossProfitLink, earningsLossToExpenseLink]);
};

export const getLossGrossProfitOutLinks = (grossProfit: number) => {
  const isNegativeGrossProfit = grossProfit < 0;

  const lossGrossProfitToCostOfSalesLink = isNegativeGrossProfit && {
    source: NODE_NAMES.LOSS_GROSS_PROFIT,
    target: NODE_NAMES.COST_OF_SALES,
    value: Math.abs(grossProfit),
    groupType: GROUP_TYPES.LOSS,
  };

  return compact([lossGrossProfitToCostOfSalesLink]);
};

export const getGrossProfitOutLinks = ({ grossProfit, earnings, expenses }) => {
  const isNegativeGrossProfit = grossProfit < 0;

  const grossProfitToEarningsGainLink = !isNegativeGrossProfit &&
    earnings >= 0 && {
      source: NODE_NAMES.GROSS_PROFIT,
      target: NODE_NAMES.EARNINGS_GAIN,
      value: earnings,
      groupType: GROUP_TYPES.PROFIT,
    };

  const grossProfittoExpensesLink = !isNegativeGrossProfit && {
    source: NODE_NAMES.GROSS_PROFIT,
    target: NODE_NAMES.EXPENSES,
    value: earnings >= 0 ? expenses : grossProfit,
    groupType: GROUP_TYPES.EXPENSES,
  };

  return compact([grossProfitToEarningsGainLink, grossProfittoExpensesLink]);
};

export const isInsufficientData = (rawData?: SafeRawDataType | null) => {
  if (!rawData) {
    return true;
  }
  const { expenses, grossProfit, revenue } = rawData;
  const isNegativeGrossProfit = grossProfit < 0;
  const isNegativeRevenue = revenue < 0;
  const isNegativeExpenses = expenses < 0;
  return isNegativeGrossProfit || isNegativeRevenue || isNegativeExpenses;
};

export const checkNonNilData = (
  data?: CompanyTimeSeries | null
): data is CompanyTimeSeries => {
  const isDataValid = data && data?.timeSeries?.length;
  if (!isDataValid) {
    return false;
  }
  return true;
};

const getTimeSeriesItem = (data: CompanyTimeSeries, endDate: string) => {
  const normalizedEndDate = new Date(endDate);
  return data.timeSeries.find((item) => {
    const itemDate = new Date(item.date);
    return itemDate.toDateString() === normalizedEndDate.toDateString();
  });
};
export const pickTimeSeriesData = (
  data: CompanyTimeSeries,
  endDate?: string
) => {
  const normalizedEndDate = endDate || getTodayDate();
  const endDateItem = getTimeSeriesItem(data, normalizedEndDate);

  if (endDateItem) {
    return endDateItem;
  }

  const bisect = bisector((d: TimeSeriesItem) => new Date(d.date));
  const index =
    bisect.left(
      data.timeSeries,
      new Date(normalizedEndDate),
      1,
      data.timeSeries.length
    ) - 1; // zero-based index
  return data.timeSeries[index];
};

export const checkValidData = (
  data?: CompanyTimeSeries | null,
  endDate?: string
): data is CompanyTimeSeries => {
  const isNonNilData = checkNonNilData(data);
  if (!isNonNilData) {
    return false;
  }

  const chosenTimeseries = pickTimeSeriesData(data, endDate);

  if (
    isNil(chosenTimeseries.revenue) ||
    isNil(chosenTimeseries.grossProfit) ||
    isNil(chosenTimeseries.netIncome)
  ) {
    return false;
  }

  return true;
};

const calcValueByUnit = (value: number | null | undefined, unit: number) =>
  isNil(value) ? value : value * unit;

export const pickRevenueSegments = (TimeSeriesItem: TimeSeriesItem) => {
  const revenueSegments = TimeSeriesItem?.businessRevenueSegmentsLtm;
  const geographicSegments = TimeSeriesItem?.geographicRevenueSegmentsLtm;

  const shouldPickGeoSegments =
    Object.keys(revenueSegments || []).length <= 1 &&
    Object.keys(revenueSegments || []).length <
      Object.keys(geographicSegments || []).length;

  // Sorry, I'm gonna cast this as I have no idea what the actual return type is meant to be
  return (shouldPickGeoSegments ? geographicSegments : revenueSegments) as
    | number
    | { [key: string]: number | null }
    | null;
};

export const normalizedSafeGqlData = (
  data: CompanyTimeSeries,
  endDate?: string
) => {
  const chosenTimeseries = pickTimeSeriesData(data, endDate);

  const currencyInfo = data.data?.currencyInfo;

  const unit = isCurrencyInfo(currencyInfo) ? currencyInfo.reporting_unit : 1;
  const revenue = calcValueByUnit(chosenTimeseries.revenue, unit) as number;
  const costOfRevenue = calcValueByUnit(chosenTimeseries.cogs, unit) as number;
  const grossProfit = calcValueByUnit(
    chosenTimeseries.grossProfit,
    unit
  ) as number;
  const earnings = calcValueByUnit(chosenTimeseries.netIncome, unit) as number;
  const expenses = grossProfit - earnings;

  const pickedRevenueSegments = pickRevenueSegments(chosenTimeseries);

  const revenueSegments = chosenTimeseries.businessRevenueSegmentsLtm;
  const geographicSegments = chosenTimeseries.geographicRevenueSegmentsLtm;

  return {
    chosenTimeseries,
    unit,
    revenue,
    costOfRevenue,
    grossProfit,
    expenses,
    earnings,
    pickedRevenueSegments,
    revenueSegments,
    geographicSegments,
  };
};

export const mapSafeGqlDataToSankeyData = (
  data: CompanyTimeSeries,
  endDate?: string
): RawDataType => {
  const {
    chosenTimeseries,
    unit,
    revenue,
    costOfRevenue,
    grossProfit,
    expenses,
    earnings,
    pickedRevenueSegments,
  } = normalizedSafeGqlData(data, endDate);

  const industryAnalysisPast = {
    revenue_segments: pickedRevenueSegments,
    general_administrative: chosenTimeseries.generalAdministrative,
    non_op_expense: chosenTimeseries.nonOperatingExpense,
    r_d_expense: chosenTimeseries.rdExpense,
    sales_marketing: chosenTimeseries.salesMarketing,
    selling_general_admin_expenses: chosenTimeseries.generalAdministrative,
    stock_based_compensation: chosenTimeseries.stockBasedCompensation,
  };

  return {
    revenue,
    costOfRevenue,
    grossProfit,
    expenses,
    earnings,
    industryAnalysisPast,
    pastRevenueUnit: unit,
  };
};

export const mapGqlDataToRawData = (
  data?: CompanyTimeSeries | null,
  endDate?: string
): RawDataType => {
  const isSufficientData = checkValidData(data, endDate);
  if (!isSufficientData) {
    return undefined;
  }

  return mapSafeGqlDataToSankeyData(data, endDate);
};
